import { ArrowDropDown, ArrowDropUp, Close, FilterList, Search } from '@mui/icons-material';
import { Box, Button, Divider, Grid, InputAdornment, MenuItem, TextField } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import React, { FC, useState } from 'react';
import { SMBSelect } from '../../../components';
import { IManagePlaceOrderReportsTo, IManagePlaceOrderRole } from '../../../models';

interface IUserFilters {
  isLoading: boolean;
  applyFilters: (clearFilters?: boolean) => void;
  searchValue: string;
  setSearch: (val: string) => void;
  setSearchedValue: (val: string) => void;
  setPage: (val: number) => void;
  handleSearch: (clearSearch?: boolean) => void;
  roles: IManagePlaceOrderRole[];
  setSelectedRoleId: (val: string | number) => void;
  selectedRoleId: number | string;
  reportsToUsers: IManagePlaceOrderReportsTo[];
  setSelectedReportsToAccount: (val: string) => void;
  selectedReportsToAccount: string;
  setHasAppliedFilters: (val: boolean) => void;
  hasAppliedFilters?: boolean;
}

export const UserFilters: FC<IUserFilters> = ({
  reportsToUsers,
  setSelectedReportsToAccount,
  selectedReportsToAccount,
  isLoading,
  applyFilters,
  searchValue,
  setSearch,
  setSearchedValue,
  setSelectedRoleId,
  roles,
  selectedRoleId,
  hasAppliedFilters,
  setHasAppliedFilters
}) => {
  const [isMobileFilterButtonOpen, toggleMobileFilter] = useState<boolean>(false);
  const classes = useStyles({ isMobileFilterButtonOpen });

  return (
    <>
      <Button
        color="secondary"
        variant="contained"
        className={classes.mobileButton}
        startIcon={<FilterList />}
        endIcon={isMobileFilterButtonOpen ? <ArrowDropUp /> : <ArrowDropDown />}
        onClick={() => {
          toggleMobileFilter(!isMobileFilterButtonOpen);
        }}
      >
        Filters
      </Button>
      {!isMobileFilterButtonOpen && <Divider className={classes.divider} />}
      <Grid container spacing={1} alignItems="center" className={classes.wrapper}>
        <Grid item xs={12} sm={6} md={2}>
          <Box display="flex">
            <TextField
              size="small"
              fullWidth
              variant="outlined"
              placeholder="Search..."
              name="search"
              value={searchValue}
              disabled={isLoading}
              InputProps={{
                startAdornment: (
                  <InputAdornment
                    position="start"
                    className={classes.searchIcon}
                    onClick={() => {
                      if (searchValue.length > 0) {
                        setHasAppliedFilters(true);
                        setSearchedValue(searchValue);
                      }
                    }}
                  >
                    <Search />
                  </InputAdornment>
                ),
                endAdornment: searchValue ? (
                  <InputAdornment
                    position="end"
                    className={classes.searchIcon}
                    onClick={() => {
                      setHasAppliedFilters(false);
                      setSearch('');
                      setSearchedValue('');
                    }}
                  >
                    <Close />
                  </InputAdornment>
                ) : null
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' && searchValue.length > 0) {
                  setHasAppliedFilters(true);
                  setSearchedValue(searchValue);
                }
              }}
              onChange={e => {
                setSearch(e.target.value);
              }}
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SMBSelect
            variant="outlined"
            handleClear={() => setSelectedRoleId('')}
            disabled={isLoading}
            name="role"
            label="Role"
            id="role"
            value={selectedRoleId}
            onChange={({ target: { value } }) => setSelectedRoleId(Number(value))}
          >
            {roles &&
              roles.map((role, index) => {
                return (
                  <MenuItem key={`${index}`} value={role.placeOrderRoleId}>
                    {role.name}
                  </MenuItem>
                );
              })}
          </SMBSelect>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SMBSelect
            disabled={isLoading}
            name="reports-to"
            variant="outlined"
            handleClear={() => setSelectedReportsToAccount('')}
            label="Reports To"
            id="reports-to"
            value={selectedReportsToAccount}
            onChange={({ target: { value } }) => setSelectedReportsToAccount(value)}
          >
            {reportsToUsers &&
              reportsToUsers.map((user, index) => {
                return (
                  <MenuItem key={`${index}`} value={user.accountName}>
                    {user.displayName}
                  </MenuItem>
                );
              })}
          </SMBSelect>
        </Grid>
        <Grid item xs={12} md={4}>
          <Button
            color="primary"
            variant="contained"
            className={classes.button}
            disabled={isLoading}
            startIcon={<FilterList />}
            onClick={() => {
              setSearchedValue(searchValue);
              setHasAppliedFilters(true);
              applyFilters();
            }}
          >
            Apply Filters
          </Button>
          {hasAppliedFilters && (
            <Button
              className={clsx(classes.button, classes.resetButton)}
              variant="contained"
              disabled={isLoading}
              color="inherit"
              startIcon={<Close />}
              onClick={() => {
                setHasAppliedFilters(false);
                setSearch('');
                setSearchedValue('');
                setSelectedRoleId('');
                setSelectedReportsToAccount('');
                applyFilters(true);
              }}
            >
              Reset
            </Button>
          )}
        </Grid>
      </Grid>
    </>
  );
};

const useStyles = makeStyles<Theme, { isMobileFilterButtonOpen?: boolean }>((theme: Theme) => ({
  mobileButton: {
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  button: {
    marginTop: 4,
    marginBottom: 4,
    height: 40,
    textTransform: 'capitalize',
    width: '100%',
    '@media (min-width: 400px)': {
      width: '48%'
    },
    [theme.breakpoints.up('sm')]: {
      width: 'auto'
    }
  },
  resetButton: {
    '@media (min-width: 400px)': {
      marginLeft: 11
    },
    [theme.breakpoints.up('sm')]: {
      marginLeft: theme.spacing(1)
    }
  },
  wrapper: ({ isMobileFilterButtonOpen }) =>
    isMobileFilterButtonOpen
      ? {
          marginTop: 10,
          marginBottom: 10,
          display: 'flex'
        }
      : {
          display: 'none',
          marginBottom: theme.spacing(1),
          [theme.breakpoints.up('sm')]: {
            display: 'flex',
            marginBottom: theme.spacing(1),
            marginTop: theme.spacing(0.5)
          }
        },
  divider: {
    display: 'block',
    marginBottom: theme.spacing(1),
    [theme.breakpoints.up('sm')]: {
      display: 'none'
    }
  },
  searchIcon: {
    cursor: 'pointer',
    color: theme.palette.grey[500]
  },
  ellipsis: {
    fontSize: 14,
    width: 'auto',
    whiteSpace: 'normal',
    overflow: 'visible',
    '@media (min-width: 960px)': {
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden'
    },
    '@media (min-width: 1400px)': {
      width: 'auto',
      whiteSpace: 'normal',
      overflow: 'visible'
    }
  }
}));
